/**
 * Base Renderer Utilities - Shared canvas rendering utilities
 *
 * This module provides common canvas manipulation functions used across
 * all icon renderers for consistent rendering and positioning.
 */

import {
  CanvasSetup,
  IconConfig,
  IconPosition,
  ThemeColors,
} from '@/components/icons/nodeIcons/types';

/**
 * Setup canvas with common configuration
 *
 * Creates a canvas element with the appropriate size, scale, and context,
 * ready for icon rendering.
 *
 * @param config - Icon configuration with dimensions and scale
 * @returns Canvas setup with element, context, size, and center coordinates
 */
export const setupCanvas = (config: IconConfig): CanvasSetup => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const size = config.displaySize * config.scale;

  canvas.width = size;
  canvas.height = size;

  const centerX = size / 2;
  const centerY = size / 2;

  ctx.clearRect(0, 0, size, size);

  return { canvas, ctx, size, centerX, centerY };
};

/**
 * Draw circle background and border
 *
 * Renders a circular background with a border stroke, used as the base
 * for all icon types.
 *
 * @param ctx - Canvas rendering context
 * @param centerX - Center X coordinate
 * @param centerY - Center Y coordinate
 * @param size - Canvas size
 * @param scale - Scale factor
 * @param colors - Theme colors for background and stroke
 * @param borderColorOverride - Optional border color override
 */
export const drawCircleBackground = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  scale: number,
  colors: ThemeColors,
  borderColorOverride?: string
): void => {
  // Draw circle background
  ctx.beginPath();
  ctx.arc(centerX, centerY, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = colors.background;
  ctx.fill();

  // Draw border
  ctx.lineWidth = scale * 8;
  ctx.strokeStyle = borderColorOverride || colors.stroke;
  ctx.stroke();
};

/**
 * Calculate icon positioning
 *
 * Computes the x, y coordinates and scale factor for positioning an icon
 * within the canvas, accounting for optional label offset.
 *
 * @param centerX - Center X coordinate
 * @param centerY - Center Y coordinate
 * @param size - Canvas size
 * @param config - Icon configuration
 * @param label - Optional label text (affects vertical positioning)
 * @returns Icon position with x, y coordinates and scale factor
 */
export const calculateIconPosition = (
  centerX: number,
  centerY: number,
  size: number,
  config: IconConfig,
  label?: string
): IconPosition => {
  const iconScale = (size * config.iconScaleRatio) / config.iconWidth;
  const x = centerX - (config.iconWidth * iconScale) / 2;
  const y =
    centerY -
    (config.iconHeight * iconScale) / 2 -
    (label ? size * config.labelOffsetRatio : 0);

  return { x, y, scale: iconScale };
};

/**
 * Draw label text on canvas
 *
 * Renders a label below the icon with proper truncation and formatting.
 * Extracts value from "name: value" format if present.
 *
 * @param ctx - Canvas rendering context
 * @param centerX - Center X coordinate
 * @param centerY - Center Y coordinate
 * @param size - Canvas size
 * @param config - Icon configuration
 * @param label - Optional label text to display
 */
export const drawLabel = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  config: IconConfig,
  label?: string
): void => {
  if (!label) return;

  // Extract just the value part if the label is in "name: value" format
  let displayValue = label;
  const colonIndex = label.indexOf(': ');
  if (colonIndex > -1) {
    displayValue = label.substring(colonIndex + 2);
  }

  // If value is empty after extraction, use the full label
  if (!displayValue.trim()) {
    displayValue = label;
  }

  // Set up font with proper size for better visibility
  const fontSize = Math.floor(size * config.fontSizeRatio);
  ctx.font = `bold ${fontSize}px Inter, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Truncate if too long
  const displayLabel =
    displayValue.length > 20
      ? displayValue.substring(0, 17) + '...'
      : displayValue;

  // Position label below the icon
  const labelY = centerY + size * config.labelYOffsetRatio;

  // Draw the label text
  ctx.fillStyle = 'rgb(255, 255, 255)'; // White text
  ctx.fillText(displayLabel, centerX, labelY);
};
